/**
 * Guards de rutas — defensa en profundidad para acceso por URL directa.
 *
 * El NavMenu ya oculta items que el usuario no puede ver, pero un externo
 * puede escribir `/riesgos` o `/configuracion/ia` en el navegador y bypassear
 * el menú. Estos componentes lo evitan: si el usuario no cumple el guard,
 * se redirige a `/` y muestra un toast informativo.
 *
 * Tres variantes:
 *   - `<RequirePerm perm="risk:view">…</RequirePerm>` — chequea permiso vía
 *     `usePermissions().can()`. Respeta la matriz editable del ADMIN_PMIS.
 *   - `<RequireTecpetrol>…</RequireTecpetrol>` — bloquea a contratistas
 *     (PLANNER/ADOR) e interventores (INT). Para módulos confidenciales del
 *     proyecto (Riesgos, Stakeholders, Configuración).
 *   - `<RequireAdminPmis>…</RequireAdminPmis>` — atajo para rutas de
 *     configuración. Equivale a `RequirePerm` con cualquier permiso que solo
 *     ADMIN_PMIS tenga por short-circuit.
 *
 * No usan `<Navigate>` directo porque el redirect debe acompañarse de un
 * toast — usamos `useEffect` + `useNavigate`. Mientras tanto el componente
 * renderiza un fallback explicativo.
 */

import { useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { MessageBar, MessageBarBody } from "@fluentui/react-components";
import { useAuth } from "@core/auth/useAuth";
import { usePermissions } from "@core/auth/usePermissions";
import { esContratista, esInterventor } from "@core/auth/scope";
import { useToast } from "@core/ui/useToast";

interface BaseGuardProps {
  children: ReactNode;
  /** Mensaje informativo mostrado al redirigir. */
  mensaje?: string;
}

/**
 * Bloquea acceso si el usuario no cumple el permiso. Redirige al home tras
 * 1s con un toast informativo. Mientras decide, muestra MessageBar.
 */
export function RequirePerm({
  perm,
  children,
  mensaje,
}: BaseGuardProps & { perm: string }) {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const toast = useToast();

  const permitido = !!user && can(perm);

  useEffect(() => {
    if (!user || permitido) return undefined;
    toast.error(
      "Acceso restringido",
      mensaje ?? "No tienes permisos para acceder a esta sección.",
    );
    const t = setTimeout(() => navigate("/", { replace: true }), 100);
    return () => clearTimeout(t);
  }, [permitido, user, navigate, toast, mensaje]);

  if (!user) return null; // AuthProvider se encarga
  if (permitido) return <>{children}</>;
  return (
    <MessageBar intent="warning" style={{ margin: "1rem" }}>
      <MessageBarBody>
        {mensaje ?? "No tienes permisos para acceder a esta sección."}
      </MessageBarBody>
    </MessageBar>
  );
}

/**
 * Solo personal Tecpetrol (no contratistas, no interventoría). Para módulos
 * con información estratégica del proyecto.
 */
export function RequireTecpetrol({ children, mensaje }: BaseGuardProps) {
  const { user } = useAuth();
  const { role } = usePermissions();
  const navigate = useNavigate();
  const toast = useToast();

  const externo = !!role && (esContratista(role) || esInterventor(role));

  useEffect(() => {
    if (!user || !externo) return undefined;
    toast.error(
      "Acceso restringido",
      mensaje ?? "Esta sección es exclusiva del personal de Tecpetrol Colombia.",
    );
    const t = setTimeout(() => navigate("/", { replace: true }), 100);
    return () => clearTimeout(t);
  }, [externo, user, navigate, toast, mensaje]);

  if (!user) return null;
  if (!externo) return <>{children}</>;
  return (
    <MessageBar intent="warning" style={{ margin: "1rem" }}>
      <MessageBarBody>
        {mensaje ??
          "Esta sección es exclusiva del personal de Tecpetrol Colombia."}
      </MessageBarBody>
    </MessageBar>
  );
}

/**
 * Atajo: solo ADMIN_PMIS. Usa `RequirePerm` con un permiso que solo
 * el ADMIN tiene por short-circuit (no se le concede a nadie en la matriz).
 */
export function RequireAdminPmis({ children, mensaje }: BaseGuardProps) {
  return (
    <RequirePerm
      perm="ia:admin"
      mensaje={
        mensaje ?? "Esta sección solo está disponible para el Administrador del PMIS."
      }
    >
      {children}
    </RequirePerm>
  );
}
